import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { LogOut, RefreshCw, User, Save, Mail, Phone, Cake, KeyRound, Link2, Copy, Check, Building2, Info, ExternalLink, ScanLine, Download, MessageCircle, AlertTriangle, Loader2 } from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import { getAuthHeaders, isPartner, logout, loginUrl } from "../lib/adminAuth";
import { LOGO_URL } from "../lib/brand";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const WEBSITE = "https://alphalane-tradingsystems.com";

export default function PartnerProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    birthdate: "",
    ref_link: "",
    roboforex_partneraccount: "",
    roboforex_ref_url: "",
    mt5_partner_id: "",
  });

  const fetchProfile = async () => {
    if (!isPartner()) {
      navigate("/partner/login");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/partner/me`, { headers: getAuthHeaders() });
      setProfile(res.data);
      setForm({
        name: res.data.name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
        birthdate: res.data.birthdate || "",
        ref_link: res.data.ref_link || "",
        roboforex_partneraccount: res.data.roboforex_partneraccount || "",
        roboforex_ref_url: res.data.roboforex_ref_url || "",
        mt5_partner_id: res.data.mt5_partner_id || "",
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        const url = loginUrl();
        logout();
        navigate(url);
      } else {
        toast.error("Profil konnte nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); /* eslint-disable-next-line */ }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.patch(`${API}/partner/me`, form, { headers: getAuthHeaders() });
      toast.success("Profil aktualisiert.");
      await fetchProfile();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  const doLogout = () => {
    const url = loginUrl();
    logout();
    navigate(url);
  };

  const personalRegLink = profile?.slug ? `${WEBSITE}/register/${profile.slug}` : "";
  // Auto-attributing lead-magnet route — supersedes the legacy /insider-report
  // for partners with a slug. Slug-less fallback keeps the old URL working.
  const personalLeadMagnetLink = profile?.slug
    ? `${WEBSITE}/ai/${profile.slug}`
    : `${WEBSITE}/insider-report`;

  const copyRefLink = () => {
    if (!personalRegLink) return;
    navigator.clipboard.writeText(personalRegLink);
    setCopied(true);
    toast.success("Reflink kopiert.");
    setTimeout(() => setCopied(false), 1800);
  };

  const downloadQR = () => {
    const canvas = document.getElementById("partner-profile-qr-canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.download = `alphalane-reflink-${profile?.slug || "qr"}.png`;
    a.href = url;
    a.click();
  };

  const downloadLeadMagnetQR = () => {
    const canvas = document.getElementById("partner-profile-qr-leadmagnet-canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.download = `alphalane-leadmagnet-${profile?.slug || "qr"}.png`;
    a.href = url;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] overflow-x-hidden">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={LOGO_URL} alt="Alphalane" className="h-8 w-8 sm:h-9 sm:w-9 object-contain flex-shrink-0" />
            <div className="font-display min-w-0">
              <div className="font-semibold text-[14px] sm:text-[15px] truncate">
                <span className="al-grad-text">ALPHALANE</span>
                <span className="hidden sm:inline text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Partner</span>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button onClick={fetchProfile} disabled={loading} className="al-cta-ghost !py-2 !px-2 sm:!px-3 text-sm" data-testid="profile-refresh" aria-label="Aktualisieren">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Aktualisieren</span>
            </button>
            <button onClick={doLogout} className="al-cta-ghost !py-2 !px-2 sm:!px-3 text-sm" data-testid="profile-logout" aria-label="Logout">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <div className="mb-7">
          <span className="al-tag">Mein Profil</span>
          <h1 className="mt-3 font-display font-bold text-2xl sm:text-3xl lg:text-4xl">
            Persönliche Daten
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Deine Stammdaten — werden auch im Kontakt mit deinen empfohlenen Kunden verwendet.
            Änderungen wirken sofort in der gesamten CRM-Ansicht.
          </p>
        </div>

        {loading ? (
          <div className="al-card p-10 text-center text-slate-500" data-testid="profile-loading">Lade…</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Linke Spalte: Edit-Formular */}
            <form onSubmit={onSave} className="al-card p-6 sm:p-8 lg:col-span-2 space-y-5" data-testid="profile-form">
              <div className="flex items-center gap-3 pb-3 border-b border-[var(--al-line)]">
                <div className="h-10 w-10 rounded-xl al-grad-bg text-white flex items-center justify-center">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg">Daten bearbeiten</div>
                  <div className="text-xs text-slate-500">Wird live an deine Kunden synchronisiert.</div>
                </div>
              </div>

              <Field icon={<User className="h-4 w-4" />} label="Name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  required
                  data-testid="profile-name"
                  className="al-input"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field icon={<Mail className="h-4 w-4" />} label="E-Mail" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    data-testid="profile-email"
                    className="al-input"
                  />
                </Field>
                <Field icon={<Phone className="h-4 w-4" />} label="Telefon">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+41 79 123 45 67"
                    data-testid="profile-phone"
                    className="al-input"
                  />
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field icon={<Cake className="h-4 w-4" />} label="Geburtsdatum">
                  <input
                    type="date"
                    value={form.birthdate}
                    onChange={update("birthdate")}
                    max={new Date().toISOString().slice(0, 10)}
                    data-testid="profile-birthdate"
                    className="al-input"
                  />
                </Field>
                <Field
                  icon={<Building2 className="h-4 w-4" />}
                  label="RoboForex Partner-Account"
                  hint="Deine RoboForex Partner-Account-Nummer (6–10-stellige Zahl). Auf dieses Konto werden Provisionen ausgezahlt."
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.roboforex_partneraccount}
                    onChange={update("roboforex_partneraccount")}
                    placeholder="z.B. 30291457"
                    data-testid="profile-roboforex"
                    className="al-input font-mono-tab"
                  />
                </Field>
              </div>

              <Field
                icon={<Link2 className="h-4 w-4" />}
                label="Affiliate-Code / Reflink (RoboForex Agent-ID)"
                hint={
                  <>
                    Dein kurzer Affiliate-Code aus dem RoboForex-Partner-Dashboard (3–8 Zeichen, nur Buchstaben + Zahlen).
                    Beispiel: <code className="bg-violet-50 px-1.5 py-0.5 rounded">zbtxc</code>.
                    Dieser Code bestimmt deinen persönlichen Reflink <code className="bg-violet-50 px-1.5 py-0.5 rounded">/register/&lt;code&gt;</code> UND die Affiliate-Zuordnung bei RoboForex —
                    es ist <strong>ein einziges Feld</strong>, das beides steuert.
                  </>
                }
              >
                <input
                  type="text"
                  value={form.ref_link}
                  onChange={(e) => {
                    // Normalise as the user types: lowercase, only a-z 0-9.
                    const cleaned = (e.target.value || "").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 20);
                    setForm((f) => ({ ...f, ref_link: cleaned }));
                  }}
                  placeholder="z.B. zbtxc"
                  maxLength={20}
                  data-testid="profile-ref-link"
                  className="al-input font-mono-tab tracking-wide"
                />
                {form.ref_link && (
                  <div className="mt-2 text-[11px] text-slate-500 font-mono-tab leading-relaxed">
                    → Dein Reflink: <a href={`${WEBSITE}/register/${form.ref_link}`} target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:underline">{WEBSITE}/register/{form.ref_link}</a>
                  </div>
                )}
              </Field>

              <Field
                icon={<Link2 className="h-4 w-4" />}
                label="Reflink Video 2 (Copy-Trading)"
              >
                <input
                  type="text"
                  value={form.mt5_partner_id}
                  onChange={update("mt5_partner_id")}
                  placeholder="https://my.roboforex.com/en/register-copyfx/?trader_account=…&partner_token=…"
                  maxLength={500}
                  data-testid="profile-mt5-partner-id"
                  className="al-input font-mono-tab text-xs"
                />
              </Field>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[var(--al-line)]">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="profile-save"
                  className="al-cta disabled:opacity-50"
                >
                  {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {busy ? "Speichere…" : "Speichern"}
                </button>
              </div>
            </form>

            {/* Rechte Spalte: Read-only Stammdaten */}
            <div className="space-y-6">
              <div className="al-card p-6" data-testid="profile-reflink-card">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                  <Link2 className="h-3.5 w-3.5" /> Dein persönlicher Reflink
                </div>
                {profile?.slug ? (
                  <>
                    <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                      Endkunden landen über diesen Link direkt auf deiner Registrierungs-Seite und werden
                      automatisch dir zugeordnet.
                    </p>

                    {/* Full URL display + copy */}
                    <div className="mt-3 rounded-xl border border-[var(--al-line)] bg-slate-50/60 p-3">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                        Registrierungs-Link
                      </div>
                      <a
                        href={personalRegLink}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="profile-reflink-url"
                        className="block text-sm font-mono-tab text-slate-800 break-all hover:text-[var(--al-purple)] transition-colors"
                      >
                        {personalRegLink}
                      </a>
                    </div>

                    {/* Insider-Report Landing Page (Lead-Magnet) — auto-attribution via /ai/{slug} */}
                    <div className="mt-2 rounded-xl border border-[var(--al-line)] bg-slate-50/60 p-3">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                        Insider-Report Landing Page (Lead-Magnet)
                      </div>
                      <a
                        href={personalLeadMagnetLink}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="profile-insider-url"
                        className="block text-sm font-mono-tab text-slate-800 break-all hover:text-[var(--al-purple)] transition-colors"
                      >
                        {personalLeadMagnetLink}
                      </a>
                      <p className="mt-1 text-[10px] text-slate-400 leading-snug">
                        {profile?.slug
                          ? "Soft-Lead-Funnel — Leads werden dir automatisch zugeordnet."
                          : "Lead-Magnet zum Teilen — Empfehlungsgeber-Feld ordnet Leads automatisch dir zu."}
                      </p>
                    </div>

                    <button
                      onClick={copyRefLink}
                      data-testid="profile-copy-reflink"
                      className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-[var(--al-line)] bg-white hover:border-[var(--al-purple)] transition-colors w-full justify-center font-semibold"
                    >
                      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? "Kopiert!" : "Link kopieren"}
                    </button>

                    {/* QR-Codes — Neukunden-Registrierung + Lead-Magnet nebeneinander */}
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="profile-qr-grid">
                      {/* QR 1: Neukunden-Registrierung */}
                      <div className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-violet-50/40 border border-violet-100" data-testid="profile-qr-block">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[var(--al-purple)] mb-1 text-center">
                          <ScanLine className="h-3 w-3 flex-shrink-0" />
                          <span>Endkunden-Registrierung</span>
                        </div>
                        <QRCodeCanvas
                          id="partner-profile-qr-canvas"
                          value={personalRegLink}
                          size={140}
                          level="H"
                          marginSize={2}
                          bgColor="#FFFFFF"
                          fgColor="#0F172A"
                          imageSettings={{ src: "/Logo.png", height: 36, width: 36, excavate: true }}
                        />
                        <p className="text-[10px] text-slate-500 text-center leading-tight max-w-[140px]">
                          Scan → Registrierungs-Seite.
                        </p>
                        <button
                          onClick={downloadQR}
                          data-testid="profile-download-qr"
                          className="text-xs font-semibold text-[var(--al-purple)] hover:underline flex items-center gap-1 mt-1"
                        >
                          <Download className="h-3 w-3" /> Download
                        </button>
                      </div>

                      {/* QR 2: Insider-Report Lead-Magnet (/ai/{slug}) */}
                      <div className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-sky-50/40 border border-sky-100" data-testid="profile-qr-leadmagnet-block">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-sky-700 mb-1 text-center">
                          <ScanLine className="h-3 w-3 flex-shrink-0" />
                          <span>Insider-Report (Lead-Magnet)</span>
                        </div>
                        <QRCodeCanvas
                          id="partner-profile-qr-leadmagnet-canvas"
                          value={personalLeadMagnetLink}
                          size={140}
                          level="H"
                          marginSize={2}
                          bgColor="#FFFFFF"
                          fgColor="#0F172A"
                          imageSettings={{ src: "/Logo.png", height: 36, width: 36, excavate: true }}
                        />
                        <p className="text-[10px] text-slate-500 text-center leading-tight max-w-[140px]">
                          Scan → Lead-Magnet, Auto-Zuordnung.
                        </p>
                        <button
                          onClick={downloadLeadMagnetQR}
                          data-testid="profile-download-leadmagnet-qr"
                          className="text-xs font-semibold text-sky-700 hover:underline flex items-center gap-1 mt-1"
                        >
                          <Download className="h-3 w-3" /> Download
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mt-3 text-xs text-slate-500 leading-relaxed">
                    Noch kein Slug zugewiesen — wende dich an Pascal.
                  </div>
                )}
              </div>

              <div className="al-card p-6 al-grad-soft-bg">
                <div className="flex items-start gap-2">
                  <Link2 className="h-4 w-4 text-[var(--al-purple)] mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    <strong className="text-slate-800">Hinweis:</strong> Slug, Reflink und Roboforex Partner-Account
                    kann nur ein Admin ändern. Wende dich bei Bedarf an Pascal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <style>{`
        .al-input {
          width: 100%;
          margin-top: 0.4rem;
          border: 1px solid var(--al-line);
          background: #fff;
          border-radius: 0.75rem;
          padding: 0.625rem 0.875rem;
          font-size: 0.875rem;
          transition: all 0.2s;
        }
        .al-input:focus {
          outline: none;
          border-color: var(--al-purple);
          box-shadow: 0 0 0 2px rgba(124,58,237,0.2);
        }
      `}</style>
    </div>
  );
}

const Field = ({ icon, label, required, hint, children }) => (
  <label className="block">
    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-500">
      {icon} {label} {required && <span className="text-rose-500">*</span>}
    </span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400 leading-relaxed">{hint}</span>}
  </label>
);
